import pino from 'pino';

import { REDACT_PATHS } from '../redaction';

/**
 * @name Logger
 * @description A production-grade logger using Pino with automatic field redaction
 *
 * Features:
 * - Automatic redaction of sensitive fields (passwords, tokens, PII)
 * - JSON structured logging for machine parsing
 * - Environment-aware log levels
 * - Browser compatibility
 *
 * Security: All fields in SENSITIVE_FIELDS are automatically redacted
 */
const Logger = pino({
  // Browser compatibility
  browser: {
    asObject: true,
  },

  // Environment-aware log level
  level:
    process.env.LOG_LEVEL ||
    (process.env.NODE_ENV === 'production' ? 'info' : 'debug'),

  // Base context included in all logs
  base: {
    env: process.env.NODE_ENV || 'development',
    service: 'ballee',
  },

  // Standardize error key
  errorKey: 'error',

  // SECURITY: Redact sensitive fields automatically (top-level + nested)
  redact: {
    paths: REDACT_PATHS,
    censor: '[REDACTED]',
  },

  // Production-optimized formatting
  ...(process.env.NODE_ENV === 'production'
    ? {
        // JSON format for production (machine-readable)
        formatters: {
          level: (label) => {
            return { level: label };
          },
        },
      }
    : process.env.VITEST
      ? {}
      : {
          // Pretty format for development (human-readable)
          //
          // pino-pretty runs via pino's worker-thread transport, which
          // asynchronously imports 'pino/pino.js' inside the worker. Under
          // Vitest, that import can still be in flight when the test's jsdom
          // environment is torn down, throwing an unhandled
          // EnvironmentTeardownError that fails the run regardless of
          // whether every assertion passed. Skipping the transport under
          // Vitest (above) avoids spawning that worker thread at all.
          transport: {
            target: 'pino-pretty',
            options: {
              colorize: true,
              translateTime: 'HH:MM:ss',
              ignore: 'pid,hostname',
            },
          },
        }),
});

export { Logger };
